import { AuthError } from './provider';
import type { AuthProvider, OperatorUser } from './provider';

/**
 * The development sign-in: an unsigned token in the shape `admin-api`'s
 * `FakeVerifier` accepts (`alg: "none"`, empty signature, `operator: true`,
 * **no `tenant_id`**), so a laptop with no Firebase project can run the whole
 * console against a local `admin-api` started with `AUTH_FAKE_TOKENS=1`.
 *
 * It is a copy of `mintDevToken` from
 * `apps/admin-api/src/auth/token-verifier.ts` rather than an import of it, for
 * the reason `wire.ts` gives: apps do not cross-import, and that module reaches
 * for `Buffer`. Fifteen lines of base64url is the cheaper of the two prices. If
 * the token shape ever changes, `auth.test.ts` fails here and the round-trip
 * test in `admin-api` fails there.
 *
 * **`operator: true` and nothing else.** `apps/app`'s equivalent mints
 * `tenant_id` and `role`; this one must not, and `mintOperatorToken` exposes
 * `operator: false` / `tenantId` only so the tests can mint the two tokens the
 * server is required to refuse. Neither is reachable from the UI.
 */

const STORAGE_KEY = 'yourstore.operator-dev-session';
const LIFETIME_SECONDS = 3600;
/** Re-mint before the server would call it expired, not after. */
const REFRESH_MARGIN_SECONDS = 300;

interface DevSession {
  uid: string;
  email: string;
}

export class FakeAuthProvider implements AuthProvider {
  readonly kind = 'fake' as const;
  private readonly storage: Storage | null;
  private readonly now: () => number;
  private readonly listeners = new Set<(user: OperatorUser | null) => void>();
  private session: DevSession | null = null;

  constructor(options: { storage?: Storage | null; now?: () => number } = {}) {
    this.storage = options.storage === undefined ? safeStorage() : options.storage;
    this.now = options.now ?? Date.now;
    this.session = this.restore();
  }

  current(): OperatorUser | null {
    return this.session === null ? null : { uid: this.session.uid, email: this.session.email };
  }

  async signIn(email: string, _password: string): Promise<void> {
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      throw new AuthError('bad_credentials', 'An email address is required.');
    }
    this.session = { uid: `op-${trimmed}`, email: trimmed };
    this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.session));
    this.emit();
  }

  async signOut(): Promise<void> {
    this.session = null;
    this.storage?.removeItem(STORAGE_KEY);
    this.emit();
  }

  async token(): Promise<string | null> {
    if (this.session === null) return null;
    return mintOperatorToken({
      uid: this.session.uid,
      email: this.session.email,
      expiresAt: Math.floor(this.now() / 1000) + LIFETIME_SECONDS + REFRESH_MARGIN_SECONDS,
    });
  }

  onChange(listener: (user: OperatorUser | null) => void): () => void {
    this.listeners.add(listener);
    listener(this.current());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.current());
  }

  private restore(): DevSession | null {
    const raw = this.storage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof (parsed as DevSession).uid === 'string'
      ) {
        return parsed as DevSession;
      }
    } catch {
      // A corrupt dev session is not worth an error path. Sign in again.
    }
    return null;
  }
}

export interface OperatorTokenOptions {
  uid?: string;
  email?: string;
  /** Defaults to `true`. `false` mints the token `admin-api` must refuse. */
  operator?: boolean;
  /** Only ever set by a test: a token carrying tenancy is refused there. */
  tenantId?: string;
  expiresAt?: number;
}

export function mintOperatorToken(options: OperatorTokenOptions = {}): string {
  const header = base64Url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = base64Url(
    JSON.stringify({
      sub: options.uid ?? 'op-dev',
      ...(options.operator === false ? {} : { operator: true }),
      ...(options.tenantId === undefined ? {} : { tenant_id: options.tenantId }),
      ...(options.email === undefined ? {} : { email: options.email }),
      exp: options.expiresAt ?? Math.floor(Date.now() / 1000) + 3600,
    }),
  );
  return `${header}.${payload}.`;
}

/** The `audit_log.actor` this session will produce — shown in the shell. */
export function actorOf(user: OperatorUser): string {
  return `operator:${user.email ?? user.uid}`;
}

function base64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}
